// LDAP / Active Directory connection UI (PB-17-07, PB-03-09; PD-590 – PD-597): connection facts,
// the shared configuration fields of the add and edit dialogs, test connection (counts only) and
// the write-only bind password. The API validates and authorises everything; nothing here is a
// security boundary.
import type { DirectoryConnectionSummary, DirectoryTestResponse } from '@passage/contracts';
import { useMutation } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { ApiRequestError, api } from '../api.ts';
import { useI18n } from '../i18n.tsx';
import { Dialog, ErrorSummary, Field } from './ui.tsx';

const fieldErrors = (e: unknown) => (e instanceof ApiRequestError ? e.fields : {});

/** Active Directory defaults for a new connection; the administrator confirms each one. */
export const LDAP_FORM_DEFAULTS = {
  url: 'ldaps://',
  startTls: false,
  caCertificate: '',
  bindDn: '',
  searchBase: '',
  scope: 'sub',
  filter: '(&(objectClass=user)(mail=*))',
  externalId: 'objectGUID',
  email: 'mail',
  displayName: 'displayName',
  employeeId: 'employeeID',
  department: 'department',
  title: 'title',
  office: 'physicalDeliveryOfficeName',
  enabledMode: 'ad_user_account_control',
  enabledAttribute: 'userAccountControl',
  disabledValues: '',
  pageSize: '500',
  timeoutMs: '15000',
};
export type LdapForm = typeof LDAP_FORM_DEFAULTS;

type Cfg = Record<string, unknown> & { attributes?: Record<string, string | null>; enabled?: { mode?: string; attribute?: string; disabledValues?: string[] } };

/** Form values from a stored connection configuration (edit dialog). */
export function ldapFormFrom(config: Record<string, unknown>): LdapForm {
  const c = config as Cfg;
  const a = c.attributes ?? {};
  const str = (v: unknown, d = '') => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : d);
  return {
    url: str(c.url, 'ldaps://'),
    startTls: c.startTls === true,
    caCertificate: str(c.caCertificate),
    bindDn: str(c.bindDn),
    searchBase: str(c.searchBase),
    scope: str(c.scope, 'sub'),
    filter: str(c.filter, LDAP_FORM_DEFAULTS.filter),
    externalId: str(a.externalId, 'objectGUID'),
    email: str(a.email, 'mail'),
    displayName: str(a.displayName, 'displayName'),
    employeeId: str(a.employeeId),
    department: str(a.department),
    title: str(a.title),
    office: str(a.office),
    enabledMode: str(c.enabled?.mode, 'ad_user_account_control'),
    enabledAttribute: str(c.enabled?.attribute, 'userAccountControl'),
    disabledValues: (c.enabled?.disabledValues ?? []).join(', '),
    pageSize: str(c.pageSize, '500'),
    timeoutMs: str(c.timeoutMs, '15000'),
  };
}

/** Configuration document sent to the API (validated there, PD-591). */
export function ldapConfigOf(f: LdapForm): Record<string, unknown> {
  const opt = (v: string) => (v.trim() ? v.trim() : null);
  return {
    url: f.url.trim(),
    startTls: f.startTls,
    caCertificate: opt(f.caCertificate),
    bindDn: f.bindDn,
    searchBase: f.searchBase,
    scope: f.scope,
    filter: f.filter,
    attributes: { externalId: f.externalId, email: f.email, displayName: f.displayName, employeeId: opt(f.employeeId), department: opt(f.department), title: opt(f.title), office: opt(f.office) },
    enabled: {
      mode: f.enabledMode,
      attribute: f.enabledMode === 'none' ? 'userAccountControl' : f.enabledAttribute,
      disabledValues: f.enabledMode === 'attribute' ? f.disabledValues.split(',').map((x) => x.trim()).filter(Boolean) : [],
    },
    pageSize: Number(f.pageSize) || 500,
    timeoutMs: Number(f.timeoutMs) || 15000,
  };
}

export function LdapConfigFields({ f, set, errors }: { f: LdapForm; set: (patch: Partial<LdapForm>) => void; errors: Record<string, string> }) {
  const { t } = useI18n();
  const text = (k: keyof LdapForm) => (e: { target: { value: string } }) => set({ [k]: e.target.value } as Partial<LdapForm>);
  const attrs = ['externalId', 'email', 'displayName', 'employeeId', 'department', 'title', 'office'] as const;
  return (
    <>
      <Field label={t('ldap.f.url')} hint={t('ldap.f.urlHint')} error={errors['config.url']}>{(p) => <input {...p} dir="ltr" value={f.url} onChange={text('url')} />}</Field>
      <label className="checks">
        <input type="checkbox" checked={f.startTls} onChange={(e) => set({ startTls: e.target.checked })} /> {t('ldap.f.startTls')}
      </label>
      {errors['config.startTls'] ? <p className="field-error">{errors['config.startTls']}</p> : null}
      <Field label={t('ldap.f.bindDn')} error={errors['config.bindDn']}>{(p) => <input {...p} dir="ltr" autoComplete="off" value={f.bindDn} onChange={text('bindDn')} />}</Field>
      <Field label={t('ldap.f.searchBase')} error={errors['config.searchBase']}>{(p) => <input {...p} dir="ltr" value={f.searchBase} onChange={text('searchBase')} />}</Field>
      <Field label={t('ldap.f.scope')} error={errors['config.scope']}>
        {(p) => (
          <select {...p} value={f.scope} onChange={text('scope')}>
            <option value="sub">{t('ldap.scope.sub')}</option>
            <option value="one">{t('ldap.scope.one')}</option>
          </select>
        )}
      </Field>
      <Field label={t('ldap.f.filter')} error={errors['config.filter']}>{(p) => <input {...p} dir="ltr" value={f.filter} onChange={text('filter')} />}</Field>
      <Field label={t('ldap.f.caCertificate')} hint={t('ldap.f.caCertificateHint')} error={errors['config.caCertificate']} full>{(p) => <textarea {...p} dir="ltr" rows={3} value={f.caCertificate} onChange={text('caCertificate')} />}</Field>
      <h3 className="full">{t('ldap.mapping')}</h3>
      <p className="tiny full">{t('ldap.mappingHint')}</p>
      {attrs.map((k) => (
        <Field key={k} label={t(`ldap.f.attr.${k}`)} error={errors[`config.attributes.${k}`]}>{(p) => <input {...p} dir="ltr" value={f[k]} onChange={text(k)} />}</Field>
      ))}
      <Field label={t('ldap.f.enabledMode')} error={errors['config.enabled.mode']}>
        {(p) => (
          <select {...p} value={f.enabledMode} onChange={text('enabledMode')}>
            {(['ad_user_account_control', 'attribute', 'none'] as const).map((m) => <option key={m} value={m}>{t(`ldap.enabled.${m}`)}</option>)}
          </select>
        )}
      </Field>
      {f.enabledMode === 'attribute' ? (
        <>
          <Field label={t('ldap.f.enabledAttribute')} error={errors['config.enabled.attribute']}>{(p) => <input {...p} dir="ltr" value={f.enabledAttribute} onChange={text('enabledAttribute')} />}</Field>
          <Field label={t('ldap.f.disabledValues')} error={errors['config.enabled.disabledValues']}>{(p) => <input {...p} dir="ltr" value={f.disabledValues} onChange={text('disabledValues')} />}</Field>
        </>
      ) : null}
      <Field label={t('ldap.f.pageSize')} error={errors['config.pageSize']}>{(p) => <input {...p} type="number" min={1} max={5000} value={f.pageSize} onChange={text('pageSize')} />}</Field>
      <Field label={t('ldap.f.timeoutMs')} error={errors['config.timeoutMs']}>{(p) => <input {...p} type="number" min={500} max={120000} value={f.timeoutMs} onChange={text('timeoutMs')} />}</Field>
    </>
  );
}

const transportOf = (c: Record<string, unknown>) => (String(c.url ?? '').toLowerCase().startsWith('ldaps://') ? 'ldaps' : c.startTls === true ? 'starttls' : 'plain');

/** Read-only connection facts (approved UI: secure endpoint, bind reference, search base, mapping). */
export function LdapFacts({ d }: { d: DirectoryConnectionSummary }) {
  const { t, tk, dateTime } = useI18n();
  const c = d.config as Cfg;
  const a = c.attributes ?? {};
  const s = d.bindSecret;
  const secret = s.source === 'sealed' ? t('ldap.secret.sealed', { version: s.version, at: s.updatedAt ? dateTime(s.updatedAt) : '—' }) : s.source === 'reference' && d.bindSecretRef ? t('ldap.secret.reference', { name: d.bindSecretRef }) : t('ldap.secret.none');
  const mapping = Object.entries(a).filter(([, v]) => !!v).map(([k, v]) => `${k} → ${v}`).join(' · ');
  return (
    <dl className="definition">
      <div><dt>{t('ldap.f.url')}</dt><dd><code data-ltr>{String(c.url ?? '')}</code> · {tk(`ldap.transport.${transportOf(c)}`)}</dd></div>
      <div><dt>{t('ldap.f.bindDn')}</dt><dd><code data-ltr>{String(c.bindDn ?? '')}</code></dd></div>
      <div><dt>{t('ldap.secret.label')}</dt><dd>{secret}</dd></div>
      <div><dt>{t('ldap.f.searchBase')}</dt><dd><code data-ltr>{String(c.searchBase ?? '')}</code> · {tk(`ldap.scope.${String(c.scope ?? 'sub')}`)}</dd></div>
      <div><dt>{t('ldap.f.filter')}</dt><dd><code data-ltr>{String(c.filter ?? '')}</code></dd></div>
      <div><dt>{t('ldap.mapping')}</dt><dd><span data-ltr>{mapping}</span></dd></div>
      <div><dt>{t('ldap.f.enabledMode')}</dt><dd>{tk(`ldap.enabled.${c.enabled?.mode ?? 'ad_user_account_control'}`)}</dd></div>
      {d.lastTest ? <div><dt>{t('ldap.test.lastLabel')}</dt><dd>{dateTime(d.lastTest.at)} · {tk(`status.${d.lastTest.outcome}`)}{d.lastTest.errorCode ? <> · {tk(`directory.error.${d.lastTest.errorCode}`)}</> : null}</dd></div> : null}
    </dl>
  );
}

/** Test connection: bind + one sample search. Shows counts only (PD-597). */
export function LdapTestResult({ r }: { r: DirectoryTestResponse }) {
  const { t, tk } = useI18n();
  if (r.outcome === 'unknown') return <p className="note warning" role="status">{t('ldap.test.unknown')}</p>;
  if (r.outcome === 'failed' || !r.counts) return <p className="note warning" role="status">{t('ldap.test.failed', { reason: tk(`directory.error.${r.errorCode ?? 'invalid_response'}`) })}</p>;
  const c = r.counts;
  return (
    <div className="note" role="status">
      <p>{t('ldap.test.ok', { sampled: c.sampled })}</p>
      <p>{t('ldap.test.counts', { withExternalId: c.withExternalId, withEmail: c.withEmail, withDisplayName: c.withDisplayName, disabled: c.disabled })}</p>
      {(Object.entries(c.mapped) as [string, number | null][]).filter(([, n]) => n !== null).map(([k, n]) => <p key={k} className="tiny">{t('ldap.test.mapped', { attribute: t(`ldap.f.attr.${k as 'employeeId'}`), count: n ?? 0, sampled: c.sampled })}</p>)}
      <p className="tiny">{t('ldap.test.privacy')}</p>
    </div>
  );
}

export function LdapEditDialog({ d, onClose, onDone }: { d: DirectoryConnectionSummary; onClose: () => void; onDone: () => void }) {
  const { t } = useI18n();
  const [f, setF] = useState<LdapForm>(() => ldapFormFrom(d.config));
  const [reason, setReason] = useState('');
  const m = useMutation({
    mutationFn: () => api('PATCH', `/api/v1/identity/directories/${d.id}`, { expectedVersion: d.version, config: ldapConfigOf(f), reason }),
    onSuccess: () => { onDone(); onClose(); },
  });
  const fe = fieldErrors(m.error);
  return (
    <Dialog open title={t('ldap.edit')} subtitle={t('ldap.editSub')} onClose={onClose}>
      <form noValidate onSubmit={(e) => { e.preventDefault(); m.mutate(); }}>
        {m.error instanceof ApiRequestError ? <ErrorSummary error={m.error} fieldIds={{}} /> : null}
        <div className="formgrid">
          <LdapConfigFields f={f} set={(p) => setF((x) => ({ ...x, ...p }))} errors={fe} />
        </div>
        <ReasonAndActions reason={reason} setReason={setReason} error={fe.reason} onClose={onClose} pending={m.isPending} submit={t('common.save')} />
      </form>
    </Dialog>
  );
}

export function LdapPasswordDialog({ d, onClose, onDone }: { d: DirectoryConnectionSummary; onClose: () => void; onDone: () => void }) {
  const { t } = useI18n();
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const m = useMutation({
    mutationFn: () => api('POST', `/api/v1/identity/directories/${d.id}/bind-secret`, { expectedVersion: d.version, bindPassword: password, reason }),
    onSuccess: () => { setPassword(''); onDone(); onClose(); },
  });
  const fe = fieldErrors(m.error);
  return (
    <Dialog open title={t('ldap.setPassword')} subtitle={t('ldap.setPasswordSub')} onClose={onClose}>
      <form noValidate onSubmit={(e) => { e.preventDefault(); m.mutate(); }}>
        {m.error instanceof ApiRequestError ? <ErrorSummary error={m.error} fieldIds={{}} /> : null}
        <Field label={t('ldap.f.bindPassword')} error={fe.bindPassword} full>{(p) => <input {...p} type="password" autoComplete="new-password" dir="ltr" value={password} onChange={(e) => setPassword(e.target.value)} />}</Field>
        <ReasonAndActions reason={reason} setReason={setReason} error={fe.reason} onClose={onClose} pending={m.isPending} submit={t('ldap.setPassword')} />
      </form>
    </Dialog>
  );
}

function ReasonAndActions({ reason, setReason, error, onClose, pending, submit }: { reason: string; setReason: (v: string) => void; error?: string | undefined; onClose: () => void; pending: boolean; submit: ReactNode }) {
  const { t } = useI18n();
  return (
    <>
      <Field label={t('common.reason')} hint={t('common.reasonHint')} error={error} full>{(p) => <textarea {...p} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />}</Field>
      <div className="form-actions">
        <button className="button" type="button" onClick={onClose}>{t('common.cancel')}</button>
        <button className="button primary" type="submit" disabled={pending}>{pending ? t('common.saving') : submit}</button>
      </div>
    </>
  );
}
